import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrIcon from "src/design-system/components/GrIcon";
import GrIconButton from "src/design-system/components/GrIconButton";
import GrButton from "src/design-system/components/GrButton";
import GrCheckbox from "src/design-system/components/GrCheckbox";

/**
 * One layout for the blocks of the workout popup (EMOM, AMRAP, rounds / reps for time,
 * superset, single exercises): a left-aligned header (title, one line, an optional control on
 * the right), small labels above movement groups, and a footer row (results on the left,
 * Mark as complete on the right).
 */

export function BlockHeader({ title, text, aside, children }) {
  return (
    <Box sx={{ mb: gr.space[4] }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: `${gr.space[2]} ${gr.space[4]}`,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box
            component="h3"
            sx={{
              m: 0,
              fontFamily: gr.font.family,
              fontSize: gr.font.size.md,
              fontWeight: gr.font.weight.semibold,
              lineHeight: gr.font.lineHeight.tight,
              color: gr.color.ink,
            }}
          >
            {title}
          </Box>
          {text ? (
            <Box sx={{ mt: gr.space[1], fontSize: gr.font.size.sm, color: gr.color.ink2 }}>
              {text}
            </Box>
          ) : null}
        </Box>
        {aside ? <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: gr.space[2] }}>{aside}</Box> : null}
      </Box>
      {children}
    </Box>
  );
}

BlockHeader.propTypes = {
  title: PropTypes.node.isRequired,
  text: PropTypes.node,
  aside: PropTypes.node,
  children: PropTypes.node,
};

// "Movements", "Minute 1" above a group of exercises.
export function BlockLabel({ children }) {
  return (
    <Box
      sx={{
        mt: gr.space[3],
        mb: gr.space[2],
        fontSize: gr.font.size.sm,
        fontWeight: gr.font.weight.medium,
        color: gr.color.ink2,
      }}
    >
      {children}
    </Box>
  );
}

BlockLabel.propTypes = { children: PropTypes.node };

// Results on the left (rounds, time…), Mark as complete on the right, hairline above.
export function BlockFooter({ children, complete }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: `${gr.space[3]} ${gr.space[6]}`,
        mt: gr.space[4],
        pt: gr.space[3],
        borderTop: `1px solid ${gr.color.line2}`,
      }}
    >
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: `${gr.space[3]} ${gr.space[6]}`, minWidth: 0 }}>
        {children}
      </Box>
      {complete ? <Box sx={{ ml: "auto" }}>{complete}</Box> : null}
    </Box>
  );
}

BlockFooter.propTypes = { children: PropTypes.node, complete: PropTypes.node };

// A label with its input on one line ("Full rounds completed [ 3 ]").
export function ResultField({ label, children }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: gr.space[2],
        fontSize: gr.font.size.sm,
        color: gr.color.ink2,
      }}
    >
      <span>{label}</span>
      {children}
    </Box>
  );
}

ResultField.propTypes = { label: PropTypes.node, children: PropTypes.node };

// Old handlers read e.target.checked; GrCheckbox hands back the value.
export function CompleteCheck({ checked, onChange, disabled }) {
  return (
    <GrCheckbox
      label="Mark as complete"
      checked={Boolean(checked)}
      disabled={disabled}
      onChange={(value) => onChange({ target: { checked: value } })}
      sx={{ alignItems: "center", "& .MuiCheckbox-root": { mt: 0 } }}
    />
  );
}

CompleteCheck.propTypes = { checked: PropTypes.bool, onChange: PropTypes.func.isRequired, disabled: PropTypes.bool };

// Small number box for results / schemes (MUI TextField sx).
export const blockNumberSx = {
  width: "64px",
  "& .MuiOutlinedInput-root": {
    height: gr.control.sm,
    borderRadius: gr.radius.control,
    fontFamily: gr.font.family,
    fontSize: gr.font.size.sm,
    background: gr.color.surface,
  },
  "& input": { padding: `0 ${gr.space[1]}`, textAlign: "center" },
  "& legend": { display: "none" },
  "& fieldset": { top: 0, borderColor: gr.color.lineStrong },
};

// Small select next to a result ("… of [exercise]").
export const blockSelectSx = {
  minWidth: "180px",
  maxWidth: "100%",
  height: gr.control.sm,
  borderRadius: gr.radius.control,
  fontFamily: gr.font.family,
  fontSize: gr.font.size.sm,
  background: gr.color.surface,
  "& .MuiSelect-select": { py: 0, display: "flex", alignItems: "center" },
  "& fieldset": { borderColor: gr.color.lineStrong },
};

// − 2 + for the superset's number of sets.
export function SetsCounter({ value, onMinus, onPlus }) {
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: gr.space[2] }}>
      <GrIconButton size="sm" aria-label="One set less" onClick={onMinus}>
        <GrIcon icon={icons.minus} />
      </GrIconButton>
      <Box
        component="span"
        aria-live="polite"
        sx={{ minWidth: gr.space[6], textAlign: "center", fontSize: gr.font.size.md, fontWeight: gr.font.weight.semibold, color: gr.color.ink }}
      >
        {value}
      </Box>
      <GrIconButton size="sm" aria-label="One set more" onClick={onPlus}>
        <GrIcon icon={icons.plus} />
      </GrIconButton>
    </Box>
  );
}

SetsCounter.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onMinus: PropTypes.func.isRequired,
  onPlus: PropTypes.func.isRequired,
};

// Bottom of an opened exercise: Hide sets on the left, Mark as complete on the right.
export function SetsFooter({ onHide, complete }) {
  return (
    <BlockFooter complete={complete}>
      <GrButton
        variant="quiet"
        size="sm"
        startIcon={<GrIcon icon={icons.chevronUp} size="sm" />}
        onClick={(e) => {
          e.preventDefault();
          onHide();
        }}
      >
        Hide sets
      </GrButton>
    </BlockFooter>
  );
}

SetsFooter.propTypes = { onHide: PropTypes.func.isRequired, complete: PropTypes.node };

// Set number at the start of a set row ("1 × reps…"): a small round number, then a quiet ×.
export const setNumberSx = {
  flex: "none",
  width: gr.icon.xl,
  height: gr.icon.xl,
  p: 0,
  display: "grid",
  placeItems: "center",
  borderRadius: gr.radius.pill,
  border: `1px solid ${gr.color.line}`,
  background: gr.color.surface,
  color: gr.color.ink2,
  fontFamily: gr.font.family,
  fontSize: gr.font.size.xs,
  fontWeight: gr.font.weight.semibold,
  lineHeight: 1,
};
export const setTimesSx = { flex: "none", fontSize: gr.icon.sm, mx: gr.space[1], color: gr.color.muted };

// EMOM round picker chip.
export const roundChipSx = (selected) => ({
  width: gr.control.sm,
  height: gr.control.sm,
  display: "inline-grid",
  placeItems: "center",
  p: 0,
  borderRadius: gr.radius.pill,
  border: `1px solid ${selected ? gr.color.brand : gr.color.lineStrong}`,
  background: selected ? gr.color.brand : gr.color.surface,
  color: selected ? gr.color.onBrand : gr.color.ink2,
  fontFamily: gr.font.family,
  fontSize: gr.font.size.sm,
  fontWeight: gr.font.weight.medium,
  cursor: "pointer",
  transition: "background-color .15s ease, border-color .15s ease, color .15s ease",
  "&:hover": { borderColor: gr.color.brand, color: selected ? gr.color.onBrand : gr.color.brandDark },
  "&:focus-visible": { outline: "none", boxShadow: gr.shadow.focus },
});
